import { Modal, Text, Badge } from '@/components/design-system'
import type { TemplateRow } from '../types'

function statusTone(status: string | null): 'success' | 'warning' | 'critical' | 'info' | undefined {
  if (status === 'APPROVED') return 'success'
  if (status === 'PENDING') return 'warning'
  if (status === 'REJECTED' || status === 'DISABLED') return 'critical'
  if (status === 'PAUSED' || status === 'IN_APPEAL') return 'info'
  return undefined
}

function buttonTypeLabel(type: string): string {
  if (type === 'URL') return 'URL'
  if (type === 'PHONE_NUMBER') return 'Telefone'
  return 'Resposta rápida'
}

function headerFormatLabel(format: string): string {
  if (format === 'IMAGE') return 'Imagem'
  if (format === 'VIDEO') return 'Vídeo'
  if (format === 'DOCUMENT') return 'Documento'
  return format
}

interface Props {
  template: TemplateRow | null
  onClose: () => void
}

export default function TemplatePreviewModal({ template, onClose }: Props) {
  const header = template?.components.find(c => c.type === 'HEADER')
  const body = template?.components.find(c => c.type === 'BODY')
  const footer = template?.components.find(c => c.type === 'FOOTER')
  const buttons = template?.components.find(c => c.type === 'BUTTONS')

  return (
    <Modal
      open={!!template}
      onClose={onClose}
      title={template?.name || ''}
      subtitle={template ? `${template.language} · ${template.category}` : ''}
      secondaryAction={{ content: 'Fechar', onAction: onClose }}
    >
      <div className="space-y-4">
        {/* Status */}
        {template?.status && (
          <div className="flex items-center gap-2">
            <Badge tone={statusTone(template.status)}>{template.status}</Badge>
            {template.status === 'REJECTED' && template.rejected_reason && (
              <span className="text-xs text-red-600">{template.rejected_reason}</span>
            )}
          </div>
        )}

        {/* Header */}
        {header && (
          <div>
            <Text size="small" color="subdued">Cabeçalho</Text>
            {header.text ? (
              <Text className="mt-1">{header.text}</Text>
            ) : header.format ? (
              <Text className="mt-1" color="subdued">{headerFormatLabel(header.format)}</Text>
            ) : null}
          </div>
        )}

        {/* Body */}
        {body?.text && (
          <div>
            <Text size="small" color="subdued">Corpo</Text>
            <div className="bg-[#FAFAFA] rounded-lg p-3 mt-1">
              <p className="text-[13px] text-[#303030] whitespace-pre-wrap leading-relaxed">{body.text}</p>
            </div>
          </div>
        )}

        {/* Footer */}
        {footer?.text && (
          <div>
            <Text size="small" color="subdued">Rodapé</Text>
            <Text size="small" color="subdued" className="mt-1">{footer.text}</Text>
          </div>
        )}

        {/* Buttons */}
        {buttons?.buttons && buttons.buttons.length > 0 && (
          <div>
            <Text size="small" color="subdued">Botões</Text>
            <div className="mt-1 space-y-1">
              {buttons.buttons.map((btn, i) => (
                <div key={i} className="flex items-center gap-2 text-[13px]">
                  <Badge tone="info">{buttonTypeLabel(btn.type)}</Badge>
                  <span className="text-[#303030]">{btn.text}</span>
                  {btn.url && <span className="text-[#616161] truncate">({btn.url})</span>}
                  {btn.phone_number && <span className="text-[#616161]">({btn.phone_number})</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
